import { useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from "@mui/material";
import { Input } from "../bits/inputs";
import { PrimaryButton } from "../bits/buttons";
import { z } from "zod";
import { css } from "@emotion/react";
import { trpc } from "../../utils/trpc";
import { LoadingSpinner } from "../bits/loading";
import type { OrganisationWithPermissions } from "../../../../api/src/types";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { SsoStatus } from "@rowan/prisma";

export type OrganisationFormData = {
  name: string;
  permissions?: number[];
  frjAccess: boolean;
  expiry: Dayjs;
  ssoStatus: SsoStatus;
};

const organisationSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type AddOrganisationFormProps = {
  organisation?: OrganisationWithPermissions;
  onSubmit: (data: OrganisationFormData) => void;
  error?: string;
};

export const AddOrganisationForm = ({
  onSubmit,
  error,
  organisation,
}: AddOrganisationFormProps) => {
  const [formData, setFormData] = useState<OrganisationFormData>({
    name: organisation?.name ?? "",
    permissions: organisation?.permissions?.map((p) => p.publicationId),
    frjAccess: organisation?.frjAccess ?? true,
    expiry: organisation?.expiry
      ? dayjs(organisation.expiry)
      : dayjs().add(1, "year"),
    ssoStatus: organisation?.ssoStatus ?? "all",
  });

  const { data: publications, isLoading } =
    trpc.adminGetPublications.useQuery();

  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const theme = useTheme();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handlePermissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const permissionId = Number(e.target.name);

    if (formData.permissions?.includes(permissionId)) {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions?.filter((p) => p !== permissionId),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: [...(prev.permissions ?? []), permissionId],
      }));
    }
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const result = organisationSchema.safeParse(formData);
    if (!result.success) {
      setErrors(
        result.error.errors.reduce(
          (acc, error) => {
            const field = error.path[0] as keyof typeof errors;
            acc[field] = error.message;
            return acc;
          },
          {} as { name?: string; email?: string },
        ),
      );
      return;
    }
    onSubmit(formData);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Input
        label="Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        error={!!errors.name}
        helperText={errors.name}
        required
      />
      <FormControl fullWidth>
        <InputLabel id="sso-status-select-label">SSO Status</InputLabel>
        <Select
          labelId="sso-status-select-label"
          id="sso-status-select"
          value={formData.ssoStatus ?? ""}
          label="SSO Status"
          onChange={(e) =>
            setFormData({
              ...formData,
              ssoStatus: e.target.value as SsoStatus,
            })
          }
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="password_only">Password Only</MenuItem>
          <MenuItem value="sso_only">SSO Only</MenuItem>
          <MenuItem value="microsoft_only">Microsoft Only</MenuItem>
          <MenuItem value="google_only">Google Only</MenuItem>
        </Select>
      </FormControl>
      {publications && publications.length > 0 && (
        <FormGroup>
          <FormLabel>Book Access</FormLabel>
          {publications.map((publication) => (
            <FormControlLabel
              key={publication.id}
              control={
                <Checkbox
                  onChange={handlePermissionChange}
                  name={String(publication.id)}
                  disabled={publication.status !== "PUBLISHED"}
                  css={css`
                    padding: 0 9px;
                  `}
                  checked={
                    !!formData.permissions?.some((p) => p === publication.id)
                  }
                />
              }
              label={
                publication.title +
                (publication.status !== "PUBLISHED"
                  ? " (" + publication.status + ")"
                  : "")
              }
            />
          ))}
        </FormGroup>
      )}
      <FormControlLabel
        label="FRJ Access"
        control={
          <Checkbox
            name="frjAccess"
            checked={formData.frjAccess}
            onChange={(e) =>
              setFormData({ ...formData, frjAccess: e.target.checked })
            }
          />
        }
        style={{ color: theme.palette.text.primary }}
      />
      <DatePicker
        label="Expiry date"
        value={formData.expiry}
        onChange={(value) =>
          setFormData({
            ...formData,
            expiry: dayjs(value),
          })
        }
      />
      <PrimaryButton type="submit">
        {organisation ? "Update Organisation" : "Add Organisation"}
      </PrimaryButton>
      <p style={{ color: theme.palette.error.main }}>{error}</p>
    </Box>
  );
};
